import React from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { ArrowRight, CheckCircle2, Zap } from 'lucide-react';
import ScrollFadeHeading from './ScrollFadeHeading';

const ProcessSection: React.FC = () => {
  const { t } = useTranslation();

  const processSteps = [
    {
      number: '01',
      title: t('process.steps.pollination.title'),
      description: t('process.steps.pollination.description'),
      detail: t('process.steps.pollination.detail', "Entre 6h et 10h du matin, quand les fleurs s'ouvrent"),
      icon: '/assets/icons/polinisation.svg',
      color: 'from-forest-100 to-forest-200'
    },
    {
      number: '02',
      title: t('process.steps.maturation.title'),
      description: t('process.steps.maturation.description'),
      detail: t('process.steps.maturation.detail', 'Surveillance quotidienne de la croissance'),
      icon: '/assets/icons/maturation.svg',
      color: 'from-coffee-50 to-coffee-100'
    },
    {
      number: '03',
      title: t('process.steps.drying.title'),
      description: t('process.steps.drying.description'),
      detail: t('process.steps.drying.detail', 'Alternance soleil et ombre pendant 3-4 mois'),
      icon: '/assets/icons/sechage.svg',
      color: 'from-forest-200 to-coffee-100'
    },
    {
      number: '04',
      title: t('process.steps.selection.title'),
      description: t('process.steps.selection.description'),
      detail: t('process.steps.selection.detail', 'Classification par longueur, couleur et arôme'),
      icon: '/assets/icons/tri.svg',
      color: 'from-coffee-100 to-forest-100'
    }
  ];

  return (
    <section id="process" className="py-24 relative overflow-hidden">
      <div className="container mx-auto px-4 relative">
        {/* Header */}
        <ScrollFadeHeading className="text-center mb-20">
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            whileInView={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            viewport={{ once: true }}
            className="inline-flex items-center px-6 py-3 bg-forest-200/10 border border-forest-200/20 rounded-full text-forest-200 text-sm font-medium mb-6"
          >
            <Zap className="mr-2 h-5 w-5" aria-hidden="true" />
            {t('process.eyebrow', 'Savoir-Faire Ancestral')}
          </motion.div>

          <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-bold text-coffee-200 mb-8 leading-tight">
            {t('process.title')}
          </h2>

          <div className="max-w-2xl mx-auto">
            <h3 className="text-xl md:text-2xl text-forest-200 font-semibold mb-6">
              {t('process.subtitle')}
            </h3>
            <p className="text-lg text-coffee-100 leading-relaxed">
              {t('process.description')}
            </p>
          </div>
        </ScrollFadeHeading>

        {/* Process Timeline */}
        <div className="relative">
          {/* Timeline Line */}
          <div className="hidden lg:block absolute top-1/2 left-0 right-0 h-1 bg-gradient-to-r from-forest-200/20 via-forest-200/40 to-forest-200/20 transform -translate-y-1/2 z-0"></div>
          
          {/* Process Steps */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 relative z-10">
            {processSteps.map((step, index) => (
              <motion.div
                key={step.number}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.15 }}
                viewport={{ once: true }}
                className="relative group"
              >
                <div className="bg-white/55 backdrop-blur-xl p-8 rounded-3xl shadow-card hover:shadow-glow transition-all duration-500 h-full border border-white/60 group-hover:border-forest-200/40 relative overflow-hidden">
                  {/* Background Gradient */}
                  <div className={`absolute inset-0 bg-gradient-to-br ${step.color} opacity-0 group-hover:opacity-5 transition-opacity duration-500`}></div>

                  {/* Step Number */}
                  <div className="absolute -top-4 -left-4 w-16 h-16 bg-gradient-to-br from-forest-200 to-forest-100 text-cream-50 rounded-full flex items-center justify-center font-bold text-lg shadow-soft z-10">
                    {step.number}
                  </div>

                  {/* Icon */}
                  <div className="w-16 h-16 sm:w-20 sm:h-20 bg-forest-200/10 border border-forest-200/15 rounded-2xl flex items-center justify-center mb-6 mx-auto group-hover:scale-110 group-hover:bg-forest-200/15 transition-all duration-300 relative z-10">
                    <img src={step.icon} alt="" aria-hidden="true" className="h-8 w-8 sm:h-10 sm:w-10" />
                  </div>

                  {/* Content */}
                  <h3 className="font-display text-xl font-bold text-coffee-200 mb-4 text-center group-hover:text-forest-200 transition-colors duration-300 relative z-10">
                    {step.title}
                  </h3>

                  <p className="text-coffee-100 text-center leading-relaxed mb-4 relative z-10">
                    {step.description}
                  </p>

                  <div className="text-xs text-forest-200 text-center font-medium bg-forest-200/10 px-3 py-2 rounded-full relative z-10">
                    {step.detail}
                  </div>
                </div>

                {/* Connector Arrow */}
                {index < processSteps.length - 1 && (
                  <div className="hidden lg:block absolute top-1/2 -right-4 w-8 h-8 transform -translate-y-1/2 z-20">
                    <motion.div
                      initial={{ scale: 0 }}
                      whileInView={{ scale: 1 }}
                      transition={{ duration: 0.4, delay: (index * 0.15) + 0.6 }}
                      viewport={{ once: true }}
                      className="w-full h-full bg-forest-200 rounded-full flex items-center justify-center shadow-lg"
                    >
                      <ArrowRight className="h-4 w-4 text-cream-50" aria-hidden="true" />
                    </motion.div>
                  </div>
                )}
              </motion.div>
            ))}
          </div>
        </div>

        {/* Bottom CTA Section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          viewport={{ once: true }}
          className="mt-20 max-w-5xl mx-auto"
        >
          <div className="relative bg-coffee-200/85 backdrop-blur-xl rounded-3xl shadow-card overflow-hidden">
            <div className="grid sm:grid-cols-[auto_1fr] gap-6 sm:gap-10 p-8 lg:p-12 items-center relative z-10">
              <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-2xl bg-forest-200 text-cream-50 flex items-center justify-center flex-shrink-0 mx-auto sm:mx-0">
                <CheckCircle2 className="h-8 w-8 sm:h-9 sm:w-9" aria-hidden="true" />
              </div>
              <div className="text-center sm:text-left">
                <h3 className="font-display text-2xl lg:text-3xl font-bold text-cream-50 mb-4">
                  {t('process.cta.title', 'L\'Excellence dans Chaque Gousse')}
                </h3>
                <p className="text-lg text-cream-100 leading-relaxed">
                  {t('process.cta.description', "Notre engagement envers l'excellence se reflète dans chaque étape de notre processus. De la pollinisation manuelle à la sélection finale, nous préservons l'authenticité et la qualité exceptionnelle de la vanille de Madagascar.")}
                </p>
              </div>
            </div>

            {/* Decorative glow */}
            <div className="absolute -bottom-20 -right-20 w-64 h-64 bg-forest-200/20 rounded-full blur-3xl" />
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default ProcessSection;